<template>
    <nio-modal
        body-class-name="p-0"
        :centered="false"
        name="automation"
        size="lg"
        :title="$t('automations.modal.title')"
        ref="modal"
        @closed="onClosed"
        @open="onOpen"
    >
        <nio-tabs class="mb-4" :tabs="tabs" v-model="activeTab" />

        <div class="px-8 pb-8 space-y-8">
            <section v-if="activeTab == 'general'">
                <h2 class="flex items-center text-2xl mb-4">
                    <nio-icon class="mr-2" name="regular/information-circle" />
                    {{ $t('automations.modal.general') }}
                </h2>

                <div class="flex space-x-6">
                    <nio-input-group class="flex-1" :label="$t('automations.modal.name')">
                        <nio-input v-model="automation.name" />
                    </nio-input-group>

                    <nio-input-group class="flex-1" :label="$t('automations.modal.type')">
                        <nio-select :options="typeOptions" v-model="automation.type" />
                    </nio-input-group>
                </div>

                <div class="flex space-x-6 mt-6">
                    <nio-input-group class="flex-1" :hint="$t('automations.modal.userLinkedHint')" :label="$t('automations.modal.userLinked')">
                        <nio-select :options="userOptions" v-model="automation.user_id" />
                    </nio-input-group>
                </div>
            </section>

            <section v-if="activeTab == 'content'">
                <h2 class="flex items-center text-2xl mb-4">
                    <nio-icon class="mr-2" name="regular/messages-bubble-text_1" />
                    {{ $t('automations.modal.content') }}
                </h2>

                <message-builder
                    appended-message="confirmation"
                    :initial-message.sync="automation.message"
                    :initial-subject.sync="automation.subject"
                    :show-subject="automation.type !== 'sms'"
                    ref="messageBuilder"
                />
            </section>

            <section v-if="activeTab == 'timeDay'">
                <h2 class="flex items-center text-2xl mb-4">
                    <nio-icon class="mr-2" name="regular/calendar-clock" />
                    {{ $t('automations.modal.timeDay') }}
                </h2>
                <div class="space-y-6">
                    <div class="flex space-x-6">
                        <nio-input-group class="flex-1" :hint="showCustomTime ? '' : $t('automations.modal.whenWillMessageBeSent')" :label="$t('automations.modal.timeOfDay')">
                            <nio-select :options="timeOptions" v-model="automation.when" />
                        </nio-input-group>
                        <nio-input-group class="flex-1" :label="$t('automations.modal.hourOfDay')" v-if="showCustomTime">
                            <template #after-label>
                                <nio-icon
                                    class="ml-1"
                                    name="bold/question-help-circle"
                                    v-tooltip="$t('automations.modal.divergingTimezones')"
                                    v-if="authUser.timezone != authUser.account.timezone"
                                />
                            </template>
                            <nio-timepicker v-model="automation.time" />
                        </nio-input-group>
                    </div>

                    <div>
                        <div class="flex items-end justify-between mb-2">
                            <span class="font-medium text-gray-700">
                                {{ $t('automations.modal.activeDays') }}
                            </span>
                            <span class="text-sm text-gray-600">
                                {{ $t('automations.modal.whenAutomationActive') }}
                            </span>
                        </div>

                        <div class="flex space-x-4 w-full">
                            <label
                                class="flex-1 flex flex-col items-center p-2 border border-gray-200 rounded cursor-pointer | transition duration-300 hover:shadow-lg"
                                :key="weekday.index"
                                v-for="weekday in weekdays"
                            >
                                <span class="mb-2 select-none">{{ weekday.name }}</span>
                                <input
                                    class="form-checkbox"
                                    type="checkbox"
                                    :value="weekday.index"
                                    v-model="automation.weekdays"
                                >
                            </label>
                        </div>
                    </div>
                </div>
            </section>

            <section v-if="activeTab == 'advanced'">
                <h2 class="flex items-center text-2xl mb-4">
                    <nio-icon class="mr-2" name="regular/cog-browser" />
                    {{ $t('automations.modal.advanced') }}
                </h2>

                <div class="flex space-x-6">
                    <nio-input-group class="flex-2" :label="$t('automations.modal.exclusions')">
                        <nio-select :options="exclusionOptions" placement="top" v-model="exclusions" />
                    </nio-input-group>
                    <nio-input-group class="flex-1" :label="$t('automations.modal.whatTime')" v-if="showExclusionTime">
                        <template #after-label>
                            <nio-icon
                                class="ml-1"
                                name="bold/question-help-circle"
                                v-tooltip="$t('automations.modal.divergingTimezones')"
                                v-if="authUser.timezone != authUser.account.timezone"
                            />
                        </template>
                        <nio-timepicker v-model="exclusionTime" />
                    </nio-input-group>

                    <nio-input-group class="flex-1" :label="$t('automations.modal.keywords')" v-if="showExclusionMake">
                        <template #after-label>
                            <nio-icon
                                class="ml-1"
                                name="bold/question-help-circle"
                                v-tooltip="$t('automations.modal.keywordsDetails')"
                            />
                        </template>
                        <nio-input v-model="excludedMakes" />
                    </nio-input-group>
                </div>
            </section>
        </div>

        <template #footer>
            <nio-button @click="close">
                {{ $t('global.general.cancel') }}
            </nio-button>
            <nio-button
                color="blue"
                :disabled="nextButtonDisabled"
                @click="onNext"
                v-if="nextButtonVisible"
            >
                {{ $t('global.general.next') }}
            </nio-button>
            <nio-button
                color="blue"
                :disabled="saveButtonDisabled"
                :loading="saving"
                @click="onSave"
                v-else-if="saveButtonVisible"
            >
                {{ saveButtonText }}
            </nio-button>
        </template>
    </nio-modal>
</template>

<script>
    import { mapState, mapGetters, mapMutations } from 'vuex';
    import { merge } from '@/utils';
    import orderBy from 'lodash/orderBy.js';
    import MessageBuilder from '@/components/MessageBuilder.vue';
    import MessageParsing from '@/mixins/MessageParsing.js';

    export default {
        components: {
            MessageBuilder,
        },

        mixins: [MessageParsing],

        data() {
            return {
                saving: false,
                loaded: false,
                exclusions: 'none',
                exclusionTime: '',
                excludedMakes: '',
                automation: {
                    name: '',
                    type: 'sms',
                    subject: {
                        fr: '',
                        en: '',
                    },
                    message: {
                        fr: '',
                        en: '',
                    },
                    weekdays: [],
                    when: '',
                    time: '',
                    exclude_appointments_before: '',
                    exclude_appointments_after: '',
                    exclude_appointments_with_make: [],
                    user_id: null,
                },
                activeTab: 'general',
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('automation', [
                'selectedAutomation',
            ]),

            ...mapGetters([
                'weekdays',
            ]),

            nextButtonVisible() {
                return this.activeTab !== this.tabs[this.tabs.length - 1].value;
            },

            nextButtonDisabled() {
                return !this.tabs.find(tab => tab.value === this.activeTab)?.valid;
            },

            saveButtonVisible() {
                return this.activeTab === this.tabs[this.tabs.length - 1].value;
            },

            saveButtonDisabled() {
                return this.tabs.some(tab => !tab.valid);
            },

            saveButtonText() {
                return this.creating ? this.$t('global.general.create') : this.$t('global.general.save');
            },

            creating() {
                return empty(this.selectedAutomation);
            },

            showCustomTime() {
                return this.automation.when.includes('day');
            },

            showExclusionTime() {
                return ['doNotSendBefore', 'doNotSendAfter'].includes(this.exclusions);
            },

            showExclusionMake() {
                return this.exclusions == 'doNotSendWithMake';
            },

            typeOptions() {
                return [
                    { value: 'sms', label: this.$t('automations.modal.sms') },
                    { value: 'email', label: this.$t('automations.modal.email') },
                    { value: 'contact_preference', label: this.$t('automations.modal.contactPreference') },
                ];
            },

            timeOptions() {
                return [
                    {
                        label: this.$t('automations.modal.timeHeader'),
                        options: [
                            { value: '1_hour_before', label: this.hourLabel(1) },
                            { value: '2_hour_before', label: this.hourLabel(2) },
                            { value: '3_hour_before', label: this.hourLabel(3) },
                            { value: '4_hour_before', label: this.hourLabel(4) },
                            { value: '5_hour_before', label: this.hourLabel(5) },
                            { value: '6_hour_before', label: this.hourLabel(6) },
                        ],
                    },
                    {
                        label: this.$t('automations.modal.dayHeader'),
                        options: [
                            { value: '0_day_before', label: `${this.$t('automations.modal.same')} ${this.$tc('automations.modal.day', -1)}` },
                            { value: '1_day_before', label: this.dayLabel(1) },
                            { value: '2_day_before', label: this.dayLabel(2) },
                            { value: '3_day_before', label: this.dayLabel(3) },
                            { value: '4_day_before', label: this.dayLabel(4) },
                            { value: '5_day_before', label: this.dayLabel(5) },
                            { value: '6_day_before', label: this.dayLabel(6) },
                        ],
                    },
                    {
                        label: this.$t('automations.modal.weekHeader'),
                        options: [
                            { value: '7_day_before', label: this.weekLabel(1) },
                            { value: '14_day_before', label: this.weekLabel(2) },
                            { value: '21_day_before', label: this.weekLabel(3) },
                            { value: '28_day_before', label: this.weekLabel(4) },
                        ],
                    },
                ];
            },

            exclusionOptions() {
                return [
                    { value: 'none', label: this.$t('automations.modal.none') },
                    { value: 'doNotSendBefore', label: this.$t('automations.modal.doNotSendBefore') },
                    { value: 'doNotSendAfter', label: this.$t('automations.modal.doNotSendAfter') },
                    { value: 'doNotSendWithMake', label: this.$t('automations.modal.doNotSendWithMake') },
                ];
            },

            userOptions() {
                const users = this.authUser.account.users.map(user => ({
                    label: user.fullName,
                    value: user.id,
                }));

                return orderBy(users, 'label');
            },

            tabs() {
                const nameIsFilled = !!this.automation.name.trim();
                const userIsFilled = !!this.automation.user_id;
                const messageIsFilled = !!this.automation.message.fr.trim() || !!this.automation.message.en.trim();
                const subjectIsFilled = this.automation.type === 'sms' || !!this.automation.subject.fr?.trim() || !!this.automation.subject.en?.trim();
                const weekdaysIsFilled = !!this.automation.weekdays.length;
                const whenIsFilled = !!this.automation.when;
                const timeIsFilled = !this.showCustomTime || !!this.automation.time;
                const timeExclusionIsFilled = !['doNotSendBefore', 'doNotSendAfter'].includes(this.exclusions) || !!this.exclusionTime.trim();
                const makeExclusionIsFilled = !['doNotSendWithMake'].includes(this.exclusions) || !!this.excludedMakes.trim();

                return [
                    {
                        value: 'general',
                        text: this.$t('automations.modal.general'),
                        valid: nameIsFilled && userIsFilled,
                    },
                    {
                        value: 'content',
                        text: this.$t('automations.modal.content'),
                        valid: messageIsFilled && subjectIsFilled,
                    },
                    {
                        value: 'timeDay',
                        text: this.$t('automations.modal.timeDay'),
                        valid: weekdaysIsFilled && whenIsFilled && timeIsFilled,
                    },
                    {
                        value: 'advanced',
                        text: this.$t('automations.modal.advanced'),
                        valid: timeExclusionIsFilled && makeExclusionIsFilled,
                    },
                ];
            },
        },

        watch: {
            showCustomTime(newValue) {
                if (newValue) {
                    return;
                }

                this.time = '';
            },

            showExclusionTime(newValue) {
                if (newValue) {
                    return;
                }

                this.exclusionTime = null;
            },
        },

        methods: {
            ...mapMutations('automation', [
                'addAutomation',
                'updateAutomation',
            ]),

            hourLabel(number) {
                return `${number} ${this.$tc('automations.modal.hour', number)} ${this.$t('automations.modal.before')}`;
            },

            dayLabel(number) {
                return `${number} ${this.$tc('automations.modal.day', number)} ${this.$t('automations.modal.before')}`;
            },

            weekLabel(number) {
                return `${number} ${this.$tc('automations.modal.week', number)} ${this.$t('automations.modal.before')}`;
            },

            toggleWeekday(weekday) {
                const existingIndex = this.automation.weekdays.indexOf(weekday);

                if (existingIndex === -1) {
                    // Add
                    this.automation.weekdays.push(weekday);
                } else {
                    // Remove
                    this.automation.weekdays.splice(existingIndex, 1);
                }
            },

            onSave() {
                if (this.creating) {
                    this.triggerCreate();
                } else {
                    this.triggerUpdate();
                }
            },

            onNext() {
                if (this.nextButtonDisabled) {
                    return;
                }

                const currentIndex = this.tabs.findIndex(tab => tab.value === this.activeTab);
                const nextTab = this.tabs[currentIndex + 1];

                if (nextTab) {
                    this.activeTab = nextTab.value;
                }
            },

            async triggerCreate() {
                if (this.saveButtonDisabled) {
                    return;
                }

                this.saving = true;
                this.automation.account_id = this.authUser.account.id;
                this.automation.message = this.duplicateTranslationIfEmpty(this.automation.message);
                this.automation.subject = this.duplicateTranslationIfEmpty(this.automation.subject);
                this.emptyOrFormatExclusions();

                try {
                    const response = await this.$axios.post('automations', this.automation);

                    this.addAutomation(response.data.data);

                    this.close();

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('appointments.automationModal.successMessage'),
                    });
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('global.errors.unknown'),
                    });
                } finally {
                    this.saving = false;
                }
            },

            async triggerUpdate() {
                if (this.saveButtonDisabled) {
                    return;
                }

                this.saving = true;
                this.automation.message = this.duplicateTranslationIfEmpty(this.automation.message);
                this.automation.subject = this.duplicateTranslationIfEmpty(this.automation.subject);
                this.emptyOrFormatExclusions();

                try {
                    const response = await this.$axios.put(`automations/${this.selectedAutomation.id}`, this.automation);

                    this.updateAutomation(response.data.data);

                    this.close();

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('automations.modal.updateMessage'),
                    });
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('global.errors.unknown'),
                    });
                } finally {
                    this.saving = false;
                }
            },

            emptyOrFormatExclusions() {
                switch (this.exclusions) {
                    case 'doNotSendBefore':
                        this.automation.exclude_appointments_before = this.exclusionTime;
                        this.automation.exclude_appointments_after = null;
                        this.automation.exclude_appointments_with_make = [];
                        this.excludedMakes = '';
                        break;

                    case 'doNotSendAfter':
                        this.automation.exclude_appointments_before = null;
                        this.automation.exclude_appointments_after = this.exclusionTime;
                        this.automation.exclude_appointments_with_make = [];
                        this.excludedMakes = '';
                        break;

                    case 'doNotSendWithMake':
                        this.automation.exclude_appointments_before = null;
                        this.automation.exclude_appointments_after = null;
                        this.automation.exclude_appointments_with_make = this.excludedMakes.split(',');
                        this.exclusionTime = null;
                        break;

                    default:
                        this.automation.exclude_appointments_before = null;
                        this.automation.exclude_appointments_after = null;
                        this.automation.exclude_appointments_with_make = [];
                        this.excludedMakes = '';
                        this.exclusionTime = null;
                        break;
                }
            },

            close() {
                this.$refs.modal.close();
            },

            onOpen() {
                if (this.creating) {
                    this.automation.user_id = this.authUser.id;
                } else {
                    merge(this.automation, this.selectedAutomation);

                    if (this.selectedAutomation.exclude_appointments_before) {
                        this.exclusionTime = this.selectedAutomation.exclude_appointments_before;
                        this.exclusions = 'doNotSendBefore';
                    }

                    if (this.selectedAutomation.exclude_appointments_after) {
                        this.exclusionTime = this.selectedAutomation.exclude_appointments_after;
                        this.exclusions = 'doNotSendAfter';
                    }

                    if (this.selectedAutomation.exclude_appointments_with_make.length) {
                        this.exclusions = 'doNotSendWithMake';
                        this.excludedMakes = this.selectedAutomation.exclude_appointments_with_make.join();
                    }
                }
            },

            onClosed() {
                this.$reset();
            },
        },
    };
</script>
